import React, { useState, useRef, useEffect, useMemo } from "react";
import CountdownTimer from "../../src/Counter.jsx";
import placeholder from "../assets/Car place holder PNG.png";
import axios from "axios";
import { Link, Bookmark } from "lucide-react";

import { useLanguage } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import SingleAuction from "./singleAuction";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { formatPrice } from "../utils/functions";
import api from "../lib/api.ts";
import BidInformation from "./BidInformation";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const DUBAI_TZ = "Asia/Dubai";

const AuctionComponent = () => {
  const { language, translate } = useLanguage();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [cardIndex, setCardIndex] = useState(0);
  const [laneData, setLaneData] = useState({});
  const [auctions, setAuctions] = useState([]);
  const [activeLane, setActiveLane] = useState("");
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [liveAuction, setLiveAuction] = useState([]);
  const [AuctionLaneStatus, setAuctionLaneStatus] = useState([]);
  const [isBookmarked, setIsBookmarked] = useState({});

  // Fetch wishlist on component mount to check which vehicles are bookmarked
  useEffect(() => {
    const fetchWishlist = async () => {
      if (!user?.id) return;

      try {
        const response = await api.get("/wishlist");
        const wishlistData = response.data?.data || response.data;

        if (wishlistData && Array.isArray(wishlistData)) {
          // Create bookmark state object from wishlist data
          const bookmarkState = {};
          wishlistData.forEach((item) => {
            bookmarkState[item.vehicleId] = true;
          });
          setIsBookmarked(bookmarkState);
        }
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };

    fetchWishlist();
  }, [user?.id]);
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  // Prebid functionality state
  const [showBidNowPopup, setShowBidNowPopup] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [bidAmount, setBidAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle bid now button click - show prebid popup
  const handleBidNow = (vehicle) => {
    // The vehicle data from auction component has the structure:
    // auctionLanes[0].auctionLaneVehicles[0].vehicle
    // But BidInformation expects: vehicle.auctionLanesVehicles

    // Find the auction lane data for this vehicle
    let auctionLaneData = null;
    if (liveAuction && liveAuction.length > 0) {
      for (const auction of liveAuction) {
        if (auction.auctionLanes && auction.auctionLanes.length > 0) {
          for (const lane of auction.auctionLanes) {
            if (
              lane.auctionLaneVehicles &&
              lane.auctionLaneVehicles.length > 0
            ) {
              const foundVehicle = lane.auctionLaneVehicles.find(
                (av) => av.vehicleId === vehicle.id,
              );
              if (foundVehicle) {
                auctionLaneData = {
                  id: foundVehicle.id,
                  auctionLane: {
                    id: lane.id,
                    name: lane.lane?.name,
                  },
                  runNo: foundVehicle.runNo,
                };
                break;
              }
            }
          }
        }
        if (auctionLaneData) break;
      }
    }

    // Ensure the vehicle has proper auctionLanesVehicles structure for BidInformation
    const vehicleWithAuctionData = {
      ...vehicle,
      // Map the auction lane data to the structure BidInformation expects
      auctionLanesVehicles: auctionLaneData ? [auctionLaneData] : [],
    };

    console.log("Found auction lane data:", auctionLaneData);
    console.log("Vehicle with auction data:", vehicleWithAuctionData);
    setSelectedVehicle(vehicleWithAuctionData);
    setShowBidNowPopup(true);
    // Set bid amount to current bid + increment, or starting bid + increment if no current bid
    const currentLatestBid = parseFloat(vehicle?.latestBid) || 0;
    const baseAmount =
      currentLatestBid > 0 ? currentLatestBid : vehicle?.startingBid;
    setBidAmount(baseAmount + (vehicle?.bidIncrement || 100));
  };

  // Handle bid now submit
  const handleBidNowSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Try to get auction lane ID from multiple sources
      let finalAuctionLaneId =
        selectedVehicle?.auctionLanesVehicles?.[0]?.auctionLane?.id;

      if (
        !finalAuctionLaneId &&
        selectedVehicle?.auctionLanesVehicles?.[0]?.id
      ) {
        finalAuctionLaneId = selectedVehicle.auctionLanesVehicles[0].id;
      }

      // Validate required fields
      if (!finalAuctionLaneId) {
        showError("Auction Lane ID is missing. Cannot submit bid now.");
        return;
      }

      if (!selectedVehicle?.id) {
        showError("Vehicle ID is missing. Cannot submit bid now.");
        return;
      }

      if (!bidAmount || bidAmount <= 0) {
        showError("Please enter a valid bid amount.");
        return;
      }

      const response = await api.post("/bidding/prebid", {
        auctionLaneId: finalAuctionLaneId,
        vehicleId: selectedVehicle.id,
        amount: bidAmount,
      });

      if (response.data.success) {
        // Update the selectedVehicle with the new bid information
        setSelectedVehicle((prevVehicle) => {
          const updatedVehicle = {
            ...prevVehicle,
            latestBid: bidAmount,
            highestBidderId: user?.id, // Set current user as highest bidder
          };
          console.log("Updated selectedVehicle with new bid:", updatedVehicle);
          return updatedVehicle;
        });

        // Also update the liveAuction data to reflect the new bid
        setLiveAuction((prevAuctions) =>
          prevAuctions.map((auction) => ({
            ...auction,
            auctionLanes: auction.auctionLanes?.map((lane) => ({
              ...lane,
              auctionLaneVehicles: lane.auctionLaneVehicles?.map((av) =>
                av.vehicleId === selectedVehicle.id
                  ? {
                      ...av,
                      vehicle: {
                        ...av.vehicle,
                        latestBid: bidAmount,
                        highestBidderId: user?.id,
                      },
                    }
                  : av,
              ),
            })),
          })),
        );

        // Also update the laneData to reflect the new bid in the vehicle cards
        setLaneData((prevLaneData) => {
          const updatedLaneData = { ...prevLaneData };
          Object.keys(updatedLaneData).forEach((laneName) => {
            updatedLaneData[laneName] = updatedLaneData[laneName].map(
              (vehicle) =>
                vehicle.vehicleId === selectedVehicle.id
                  ? {
                      ...vehicle,
                      vehicle: {
                        ...vehicle.vehicle,
                        latestBid: bidAmount,
                        highestBidderId: user?.id,
                      },
                    }
                  : vehicle,
            );
          });
          console.log("Updated laneData with new bid:", updatedLaneData);
          return updatedLaneData;
        });

        // Calculate next bid amount
        const currentBid = parseFloat(bidAmount);
        const increment = parseFloat(selectedVehicle?.bidIncrement) || 100;
        const nextBidAmount = currentBid + increment;
        setBidAmount(nextBidAmount.toString());

        showSuccess("Bid now submitted successfully!");
      } else {
        showError(
          response.data.message ||
            "Failed to submit bid now. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error submitting bid now:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to submit bid now. Please try again.";
      showError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close prebid popup
  const closeBidNowPopup = () => {
    setShowBidNowPopup(false);
    setSelectedVehicle(null);
  };

  // Toggle wishlist item
  const toggleWishlist = async (vehicleId) => {
    if (!user?.id) {
      // Redirect to login if not authenticated
      navigate("/login");
      return;
    }

    try {
      const response = await api.post("/wishlist/toggle", { vehicleId });

      if (response.data.success) {
        const wasBookmarked = isBookmarked[vehicleId] || false;
        setIsBookmarked((prev) => ({
          ...prev,
          [vehicleId]: !wasBookmarked,
        }));

        // Show appropriate toast message based on action
        if (wasBookmarked) {
          showSuccess("Vehicle removed from Watch List successfully!");
        } else {
          showSuccess("Vehicle added to Watch List successfully!");
        }
      } else {
        showError("Failed to update Watch List. Please try again.");
      }
    } catch (error) {
      console.error("Error toggling Watch List:", error);
      showError("Failed to update Watch List. Please try again.");
    }
  };

  // Helper function to convert date to Dubai timezone
  const toDubaiDateStr = (dateStr) => {
    if (!dateStr) return dayjs().tz(DUBAI_TZ).format("YYYY-MM-DD");
    try {
      return dayjs(dateStr).tz(DUBAI_TZ).format("YYYY-MM-DD");
    } catch (error) {
      console.warn("Error formatting date:", dateStr, error);
      return dayjs().tz(DUBAI_TZ).format("YYYY-MM-DD");
    }
  };

  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        // Fetch live auctions first
        const res2 = await axios.get(
          `${process.env.REACT_APP_API_URL}/auctions/paginated`,
          {
            params: {
              page: 1,
              limit: 50,
              orderBy: "startDate",
              order: "ASC",
              search: "",
              slug: "auctions",
              type: "live",
              filters: {
                unSoldVehicles: 1,
              },
            },
          },
        );

        const LiveAuction = res2.data.data || [];
        setLiveAuction(LiveAuction);

        // Create a map of auction ID to lane statuses
        const laneStatusMap = {};
        LiveAuction.forEach((auction) => {
          if (auction.auctionLanes) {
            laneStatusMap[auction.id] = auction.auctionLanes.map((lane) => ({
              laneId: lane.id || lane.laneId,
              status:
                typeof lane.auctionStatus === "string"
                  ? lane.auctionStatus
                  : lane.auctionStatus?.name ||
                    lane.auctionStatus?.slug ||
                    lane.status,
            }));
          }
        });
        setAuctionLaneStatus(laneStatusMap);

        // Fetch upcoming auctions
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/auctions/paginated`,
          {
//             params: {
//   type: "upcomming,live",
//   orderBy: "startDate",
//   order: "ASC",
//   limit: 50,
//   filters: {
//     isActive: true,
//     unSoldVehicles: 1,
//   },
// }
            params: {
              type: "upcomming,live",
              orderBy: "startDate",
              order: "ASC",
              filters: { isActive: true },
              limit: 1,
              filters: {
                unSoldVehicles: 1,
              },
            },
          },
        );

        const allAuctions = [...LiveAuction, ...res.data.data];
        console.log("auction data from auctioncomponent page :", allAuctions);

        // Map auctions for UI (preserve auctionLanes and vehicles)
        const mapped = allAuctions.map((a) => {
          // Ensure we have valid auction data
          if (!a || typeof a !== "object") {
            console.warn("Invalid auction data:", a);
            return null;
          }

          // Ensure auction name is a string
          let auctionName = `Auction ${a.id}`;
          if (typeof a.name === "string" && a.name.trim()) {
            auctionName = a.name.trim();
          } else if (typeof a.auction === "string" && a.auction.trim()) {
            auctionName = a.auction.trim();
          }

          const auctionLanes =
            a.auctionLanes
              ?.map((lane, idx) => {
                // Ensure lane is a valid object
                if (!lane || typeof lane !== "object") {
                  console.warn("Invalid lane data:", lane);
                  return null;
                }

                const vehicles = lane.auctionLaneVehicles || [];
                const laneName = lane.lane?.name || lane.name;

                // Find the status for this specific lane
                const mappedStatus = AuctionLaneStatus[a.id]?.find(
                  (status) => status.laneId === (lane.id || lane.laneId),
                )?.status;

                const laneStatus =
                  mappedStatus ||
                  (typeof lane.auctionStatus === "string"
                    ? lane.auctionStatus
                    : lane.auctionStatus?.name || lane.auctionStatus?.slug) ||
                  (typeof lane.status === "string"
                    ? lane.status
                    : lane.status?.name || lane.status?.slug) ||
                  "unknown";

                return {
                  id:
                    typeof lane.id === "number" || typeof lane.id === "string"
                      ? lane.id
                      : idx,
                  name:
                    typeof laneName === "string" ? laneName : `Lane ${idx + 1}`,
                  vehicles,
                  status: laneStatus, // Add status to each lane
                  totalVehicles: Array.isArray(vehicles) ? vehicles.length : 0,
                };
              })
              .filter((lane) => lane !== null) || [];

          const totalCars = auctionLanes.reduce(
            (sum, lane) =>
              sum +
              (typeof lane?.totalVehicles === "number"
                ? lane.totalVehicles
                : 0),
            0,
          );

          return {
            id: a.id,
            auction: auctionName,
            name: a.name, // Keep original name property
            description: a.description,
            date: toDubaiDateStr(a.startDate),
            startDate: a.startDate || new Date().toISOString(),
            branch: a.branch || null,
            status: a.status, // Pass through auction status
            type: a.type, // Pass through auction type
            auctionLanes, // keep lanes with vehicles (now includes status for each lane)
            totalCars,
          };
        });

        // Filter out any null entries and auctions with 0 vehicles
        const validMapped = mapped.filter(
          (auction) => auction !== null && auction.totalCars > 0,
        );

        // Additional validation to ensure all required properties are strings/numbers
        const validatedMapped = validMapped.map((auction) => {
          if (typeof auction.auction !== "string") {
            console.warn(
              "Invalid auction name type:",
              typeof auction.auction,
              auction.auction,
            );
            return {
              ...auction,
              auction: `Auction ${auction.id}`,
            };
          }
          if (typeof auction.totalCars !== "number") {
            console.warn(
              "Invalid totalCars type:",
              typeof auction.totalCars,
              auction.totalCars,
            );
            return {
              ...auction,
              totalCars: 0,
            };
          }
          return auction;
        });

        setAuctions(validatedMapped);
//===========================wednesday fix show the today live auction=============================
        //previous Find the most recent auction (either past or future) using Dubai timezone
        // const now = dayjs().tz(DUBAI_TZ);
        // let nextAuction = null;
        // let minDiff = Infinity;

        // validatedMapped.forEach((a) => {
        //   if (!a.startDate) return;
        //   try {
        //     const auctionTime = dayjs.utc(a.startDate).tz(DUBAI_TZ);
        //     const diff = auctionTime.diff(now);
        //     // Include both past and future auctions, prioritize future ones
        //     if (diff >= 0 && diff < minDiff) {
        //       minDiff = diff;
        //       nextAuction = a;
        //     } else if (nextAuction === null && diff < 0) {
        //       // If no future auction found, use the most recent past auction
        //       const pastDiff = Math.abs(diff);
        //       if (pastDiff < minDiff) {
        //         minDiff = pastDiff;
        //         nextAuction = a;
        //       }
        //     }
        //   } catch (error) {
        //     console.warn("Error processing auction time:", a.startDate, error);
        //   }
        // });

        // setSelectedAuction(nextAuction);

         
        const now = dayjs().tz(DUBAI_TZ);
        const todayStr = now.format("YYYY-MM-DD");

        // Step 1: prepare auctions with Dubai time
        const auctionsWithTime = validatedMapped
          .filter((a) => a?.startDate)
          .map((a) => ({
            ...a,
            dubaiTime: dayjs.utc(a.startDate).tz(DUBAI_TZ),
          }));

        let selected = null;

        // Step 2: TODAY auctions
        const todayAuctions = auctionsWithTime
          .filter((a) => a.dubaiTime.format("YYYY-MM-DD") === todayStr)
          .sort((a, b) => a.dubaiTime.valueOf() - b.dubaiTime.valueOf());

        if (todayAuctions.length > 0) {
          // pick next upcoming today OR last one if all passed
          selected =
            todayAuctions.find((a) => a.dubaiTime.isAfter(now)) ||
            todayAuctions[todayAuctions.length - 1];
        } else {
          // Step 3: FUTURE auctions
          const futureAuctions = auctionsWithTime
            .filter((a) => a.dubaiTime.isAfter(now))
            .sort((a, b) => a.dubaiTime.valueOf() - b.dubaiTime.valueOf());

          if (futureAuctions.length > 0) {
            selected = futureAuctions[0];
          } else {
            // Step 4: PAST fallback
            const pastAuctions = auctionsWithTime
              .filter((a) => a.dubaiTime.isBefore(now))
              .sort((a, b) => b.dubaiTime.valueOf() - a.dubaiTime.valueOf());

            selected = pastAuctions[0] || null;
          }
        }

        setSelectedAuction(selected);
///=============================wedneday fix end ===============================  

      } catch (err) {
        console.error("Error fetching auctions", err);
        setLaneData({});
        setActiveLane("");
      }
    };
    fetchAuctions();
  }, []);
  const scrollLeft = () => {
    if (cardIndex > 0) {
      setCardIndex((prev) => prev - 1);
      sliderRef.current?.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    const maxIndex = (laneData[activeLane]?.length || 0) - 1;
    if (cardIndex < maxIndex) {
      setCardIndex((prev) => prev + 1);
      sliderRef.current?.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  // Function to update lanes when selected auction changes
  const updateLanesForSelectedAuction = (auction) => {
    if (!auction || !auction.auctionLanes) {
      setLaneData({});
      setActiveLane("");
      return;
    }

    console.log(
      "Updating lanes for auction:",
      auction.auction || auction.name,
      "Lanes:",
      auction.auctionLanes,
    );

    const lanes = {};
    const laneList = Array.isArray(auction.auctionLanes)
      ? [...auction.auctionLanes]
      : [];

    // Sort lanes coming from API (defensive)
    laneList
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      )
      .forEach((laneObj) => {
        const laneName = laneObj.name || `Lane ${laneObj.id}`;
        const vehicles = (laneObj.vehicles || []).map((vehicle) => ({
          ...vehicle,
          startDate: auction.startDate,
        }));
        lanes[laneName] = vehicles;
        console.log(`Lane ${laneName}: ${vehicles.length} vehicles`);
      });

    // Ensure laneData keys are ordered by lane name ASC
    const sortedNames = Object.keys(lanes).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
    const orderedLanes = {};
    sortedNames.forEach((name) => {
      orderedLanes[name] = lanes[name];
    });

    console.log("Final lane data:", orderedLanes);
    setLaneData(orderedLanes);
    setActiveLane(sortedNames[0] || "");
    setCardIndex(0); // Reset carousel to first item
  };

  // Update lanes when selectedAuction changes
  useEffect(() => {
    if (selectedAuction) {
      updateLanesForSelectedAuction(selectedAuction);
    }
  }, [selectedAuction]);

  // Don't render the component if there are no auctions
  if (!auctions || auctions.length === 0) {
    return null;
  }

  return (
    <>
      <div className="bg-[#111111] text-white p-4 sm:p-6 md:p-8 lg:p-10 max-w-7xl mx-auto font-Poppins">
        <div className="grid grid-cols-1 lg:grid-cols-[26%_74%] gap-4 md:gap-6">
          <div className="w-full">
            {selectedAuction &&
            (selectedAuction.auction || selectedAuction.name) ? (
              <SingleAuction
                auction={selectedAuction}
                // auctions={auctions}
                isSelected={true}
                onSelect={setSelectedAuction}
                index={0}
              />
            ) : (
              <div className="p-6 text-center text-gray-500">
                {translate("auctionComponent.noAuctionAvailable")}{" "}
                {translate("noAuctionToday")}
              </div>
            )}
          </div>

          <div className="flex-1 w-full overflow-hidden">
            {/* Tabs Lanes */}
            <div className="relative flex border-2 sm:border-[3px] md:border-[4px] border-[#f15a29] bg-[#F1111111] overflow-hidden rounded-sm md:rounded-none">
              {/* Sliding highlight background */}
              <div
                className="absolute top-0 bottom-0 w-1/3 bg-[#F15A29]  transition-all duration-500 ease-in-out z-0"
                style={{
                  left: `${
                    (Object.keys(laneData).indexOf(activeLane) /
                      Object.keys(laneData).length) *
                    100
                  }%`,
                  width: `${100 / (Object.keys(laneData).length || 1)}%`,
                }}
              ></div>

              {Object.keys(laneData).map((lane, index) => (
                <div
                  key={lane}
                  className={`flex-1 text-center py-2 sm:py-2.5 md:py-3 font-medium cursor-pointer relative z-10 transition-colors duration-300 text-sm sm:text-base md:text-lg
        ${lane === activeLane ? "text-white" : "text-white/70"}
        ${index === 0 ? "rounded-l-[0px]" : ""}
        ${index === Object.keys(laneData).length - 1 ? "rounded-r-[0px]" : ""}
      `}
                  onClick={() => {
                    setActiveLane(lane);
                    setCardIndex(0);
                  }}
                >
                  {lane}
                  <p
                    className={`text-[10px] xs:text-xs sm:text-xs md:text-sm transition-colors duration-300 px-1 ${
                      lane === activeLane ? "text-white" : "text-gray-300"
                    }`}
                  >
                    {laneData[lane]?.length || 0}{" "}
                    <span className="hidden sm:inline">
                      {translate("auctionComponent.carsForAuction")}
                    </span>
                    <span className="sm:hidden">{translate("cars")}</span>
                  </p>
                </div>
              ))}
            </div>

            {/* Cards */}
            <div className="relative p-2 sm:p-3 md:p-4 bg-[#FFFFFF1F] mt-2 pb-8 sm:pb-10 md:pb-12 border-[1px] sm:border-[2px] border-[#FFFFFF3D] rounded-md md:rounded-none">
              <div
                className="flex gap-2 sm:gap-3 md:gap-4 overflow-x-auto scroll-smooth"
                ref={sliderRef}
                style={{ scrollbarWidth: "none" }}
              >
                {(laneData[activeLane] || []).length === 0 ? (
                  <div className="text-white p-8">
                    {translate("auctionComponent.noVehicles")}
                  </div>
                ) : (
                  (laneData[activeLane] || [])
                    .slice() // create a copy to avoid mutating state
                    .sort((a, b) => (a.runNo ?? 0) - (b.runNo ?? 0))
                    .map((car) => (
                      <div
                        key={car.id}
                        className="bg-white text-black min-w-[240px] sm:min-w-[260px] md:min-w-[280px] max-w-[240px] sm:max-w-[260px] md:max-w-[280px] flex-shrink-0"
                      >
                        <div className="relative">
                          <div className="absolute top-1 left-1  text-white px-1 py-1 rounded-md">
                            {car.vehicle?.Highlight?.name && (
                              <span
                                className="text-xs font-sm px-1 py-1 rounded"
                                style={{
                                  backgroundColor:
                                    car.vehicle?.Highlight?.bgColor,
                                  color: car.vehicle?.Highlight?.textColor,
                                }}
                              >
                                {car.vehicle?.Highlight?.name}
                              </span>
                            )}
                          </div>
                          {/* Bookmark */}
                          <button
                            onClick={() => toggleWishlist(car?.vehicle?.id)}
                            className={`absolute top-2 right-2 sm:top-3 sm:right-3 rounded-full p-1.5 sm:p-2 md:p-3 shadow transition-colors ${
                              isBookmarked[car?.vehicle?.id]
                                ? "bg-orange-500 text-white hover:bg-orange-600"
                                : "bg-white text-gray-600 hover:bg-gray-100"
                            }`}
                          >
                            <Bookmark
                              className={`w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 ${
                                isBookmarked[car?.vehicle?.id]
                                  ? "fill-current text-white"
                                  : "text-gray-600"
                              }`}
                            />
                          </button>
                          <div className="h-32 sm:h-36 md:h-40 bg-gray-200 flex items-center justify-center overflow-hidden">
                            {/* <img
                            src={
                              car.vehicle?.imageUrl ||
                              car.vehicle.images ||
                              "https://c4.wallpaperflare.com/wallpaper/167/540/977/lamborghini-revuelto-car-lamborghini-orange-cars-vehicle-hd-wallpaper-preview.jpg"
                            }
                            alt={car.title}
                            className='h-full object-cover w-full'
                          /> */}
                            <img
                              src={
                                car.vehicle?.imageUrl ||
                                (Array.isArray(car.vehicle?.images) &&
                                car.vehicle.images.length > 0
                                  ? car.vehicle.images[0].imageUrl
                                  : placeholder)
                              }
                              alt={car.vehicle?.name}
                              className="h-full object-cover w-full"
                            />
                          </div>
                          <button
                            className="absolute bottom-[0px] right-0 bg-[#F15A29] text-white text-[10px] sm:text-xs px-2 sm:px-3 md:px-4 py-0.5 sm:py-1 shadow-md z-10 rounded-l-md rounded-r-none"
                            style={{ transform: "translateY(50%)" }}
                          >
                            {car.runNo} of {laneData[activeLane]?.length || 0}
                          </button>
                        </div>

                        <div className="p-1.5 sm:p-2">
                          <div className="mt-1">
                            <h3
                              className="font-[600] text-xs sm:text-[13px] md:text-[14px] mb-1 uppercase whitespace-nowrap block overflow-hidden text-ellipsis"
                              style={{ maxWidth: "100%" }}
                            >
                              {car.vehicle?.name}
                            </h3>
                            <div className="flex justify-between items-center text-xs sm:text-sm mb-2 gap-1 sm:gap-2">
                              <div className="flex-1">
                                <div className="flex flex-col items-center text-[#050B20CC] font-bold text-[10px] sm:text-[11px] md:text-[12px]">
                                  {translate("vehicle.labels.lotNo")}
                                </div>

                                <div className="text-[9px] sm:text-[10px] md:text-[11px] flex flex-col items-center">
                                  {car.vehicle?.lotNumber}

                                  {/* {car.vehicle.Highlight?.name} */}
                                </div>
                              </div>

                              <div className="flex-1 flex flex-col items-center">
                                <div className="text-[#050B20CC] font-bold text-[10px] sm:text-[11px] md:text-[12px] flex flex-col items-center">
                                  {translate("vehicle.labels.startingBid")}
                                </div>
                                <div className="text-[9px] sm:text-[10px] md:text-[11px] flex flex-col items-center">
                                  {formatPrice(
                                    car?.vehicle?.startingBid,
                                    "line-through",
                                    "11",
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col items-center">
                                <div className="text-[#050B20CC] font-bold text-[10px] sm:text-[11px] md:text-[12px] flex flex-col items-center">
                                  {translate("vehicle.labels.currentBid")}
                                </div>

                                <div className="flex gap-[2px] text-[9px] sm:text-[10px] md:text-[11px] flex-col items-center text-[#F15A29] font-semibold">
                                  {formatPrice(
                                    car?.vehicle?.latestBid,
                                    "orange-600",
                                  )}
                                </div>
                              </div>
                            </div>
                            <hr className="my-1.5 sm:my-2 border-gray-200" />

                            <div className="flex justify-center items-start gap-2 sm:gap-4 md:gap-6 text-center">
                              <div className="flex flex-col items-center text-[10px] sm:text-[11px] md:text-[12px] w-1/4">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="w-3 h-3 sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]"
                                  viewBox="0 0 22 18"
                                  fill="none"
                                >
                                  <path
                                    d="M21.7839 11.0194C21.7839 13.4529 21.0682 15.6239 19.6368 17.5324C19.4936 17.7233 19.3028 17.8306 19.0642 17.8545C18.8256 17.8784 18.6109 17.8187 18.4201 17.6756C18.2292 17.5324 18.1219 17.3535 18.098 17.1388C18.0741 16.9241 18.1338 16.7213 18.2769 16.5304C19.5175 14.9081 20.1378 13.0712 20.1378 11.0194C20.1378 9.39715 19.7322 7.89415 18.9211 6.51044C18.1099 5.12672 17.0006 4.02929 15.593 3.21815C14.1854 2.40701 12.6705 2.00144 11.0482 2.00144C9.42593 2.00144 7.911 2.40701 6.50343 3.21815C5.09586 4.02929 3.9865 5.12672 3.17536 6.51044C2.36421 7.89415 1.95864 9.39715 1.95864 11.0194C1.95864 13.0712 2.57893 14.9081 3.8195 16.5304C3.96264 16.7213 4.02229 16.9241 3.99843 17.1388C3.97457 17.3535 3.86721 17.5324 3.67636 17.6756C3.4855 17.8187 3.27079 17.8784 3.03221 17.8545C2.79364 17.8306 2.60279 17.7233 2.45964 17.5324C1.02821 15.6239 0.3125 13.4529 0.3125 11.0194C0.3125 9.11086 0.789643 7.33351 1.74393 5.68736C2.69821 4.04122 3.99843 2.74101 5.64457 1.78672C7.29072 0.832438 9.09193 0.355293 11.0482 0.355293C13.0045 0.355293 14.8057 0.832438 16.4519 1.78672C18.098 2.74101 19.3982 4.04122 20.3525 5.68736C21.3068 7.33351 21.7839 9.11086 21.7839 11.0194ZM16.3445 6.00944C16.5354 6.15258 16.6308 6.34344 16.6308 6.58201C16.6308 6.82058 16.5354 7.01144 16.3445 7.15458L13.6248 9.94586C13.8634 10.3753 13.9826 10.8286 13.9826 11.3057C13.9826 12.1169 13.6964 12.8087 13.1238 13.3813C12.5512 13.9539 11.8594 14.2401 11.0482 14.2401C10.2371 14.2401 9.54522 13.9539 8.97264 13.3813C8.40007 12.8087 8.11379 12.1169 8.11379 11.3057C8.11379 10.4946 8.40007 9.80272 8.97264 9.23015C9.54522 8.65758 10.2371 8.37129 11.0482 8.37129C11.5254 8.37129 11.9786 8.49058 12.4081 8.72915L15.1994 6.00944C15.3425 5.81858 15.5334 5.72315 15.7719 5.72315C16.0105 5.72315 16.2014 5.81858 16.3445 6.00944ZM12.3365 11.3057C12.3365 10.9717 12.2053 10.6854 11.9429 10.4469C11.6804 10.2083 11.3822 10.089 11.0482 10.089C10.7142 10.089 10.416 10.2083 10.1536 10.4469C9.89114 10.6854 9.75993 10.9717 9.75993 11.3057C9.75993 11.6397 9.89114 11.9379 10.1536 12.2004C10.416 12.4628 10.7142 12.594 11.0482 12.594C11.3822 12.594 11.6804 12.4628 11.9429 12.2004C12.2053 11.9379 12.3365 11.6397 12.3365 11.3057Z"
                                    fill="#DE542E"
                                  />
                                </svg>

                                {car.vehicle?.mileage + " Mi* " || "-"}
                              </div>
                              <div className="flex flex-col items-center text-[10px] sm:text-[11px] w-1/4">
                                <svg
                                  className="w-3 h-3 sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]"
                                  viewBox="0 0 15 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M0.77832 16.0814V2.50466C0.77832 2.04433 0.932487 1.65999 1.24082 1.35166C1.54915 1.04333 1.93349 0.88916 2.39382 0.88916H7.16282C7.62315 0.88916 8.00749 1.04333 8.31582 1.35166C8.62415 1.65999 8.77832 2.04433 8.77832 2.50466V8.50466H9.70132C10.1457 8.50466 10.526 8.66283 10.8423 8.97916C11.1587 9.29549 11.3168 9.67574 11.3168 10.1199V14.6969C11.3168 15.0571 11.4447 15.3651 11.7003 15.6209C11.9562 15.8767 12.2642 16.0047 12.6246 16.0047C12.9847 16.0047 13.2927 15.8767 13.5486 15.6209C13.8042 15.3651 13.9321 15.0571 13.9321 14.6969V7.88141C13.7821 7.97758 13.6237 8.04783 13.4571 8.09216C13.2904 8.13633 13.1154 8.15841 12.9321 8.15841C12.4044 8.15841 11.9584 7.97624 11.5941 7.61191C11.2297 7.24758 11.0476 6.80149 11.0476 6.27366C11.0476 5.81733 11.1838 5.41516 11.4563 5.06716C11.7287 4.71899 12.0872 4.50449 12.5321 4.42366L10.4378 2.32966C10.352 2.24366 10.3059 2.14233 10.2996 2.02566C10.2931 1.90899 10.3411 1.79941 10.4436 1.69691C10.5347 1.60591 10.6364 1.55941 10.7486 1.55741C10.8607 1.55558 10.9662 1.60208 11.0648 1.69691L14.1821 4.79691C14.3809 4.99558 14.5364 5.22374 14.6486 5.48141C14.7607 5.73908 14.8168 6.00316 14.8168 6.27366V14.6969C14.8168 15.3107 14.6052 15.8296 14.1821 16.2534C13.7591 16.6772 13.2412 16.8892 12.6283 16.8892C12.0155 16.8892 11.4962 16.6772 11.0706 16.2534C10.6449 15.8296 10.4321 15.3107 10.4321 14.6969V10.0047C10.4321 9.82516 10.3744 9.67766 10.2591 9.56216C10.1437 9.44683 9.99632 9.38916 9.81682 9.38916H8.77832V16.0814C8.77832 16.3102 8.70165 16.5021 8.54832 16.6569C8.39499 16.8117 8.20499 16.8892 7.97832 16.8892H1.57807C1.35124 16.8892 1.16124 16.8117 1.00807 16.6569C0.854904 16.5021 0.77832 16.3102 0.77832 16.0814ZM1.77832 7.38916H7.77832V2.50466C7.77832 2.35066 7.71424 2.20958 7.58607 2.08141C7.4579 1.95324 7.31682 1.88916 7.16282 1.88916H2.39382C2.23982 1.88916 2.09874 1.95324 1.97057 2.08141C1.8424 2.20958 1.77832 2.35066 1.77832 2.50466V7.38916ZM12.9321 7.27366C13.2154 7.27366 13.4529 7.17783 13.6446 6.98616C13.8362 6.79449 13.9321 6.55699 13.9321 6.27366C13.9321 5.99033 13.8362 5.75283 13.6446 5.56116C13.4529 5.36949 13.2154 5.27366 12.9321 5.27366C12.6487 5.27366 12.4112 5.36949 12.2196 5.56116C12.0279 5.75283 11.9321 5.99033 11.9321 6.27366C11.9321 6.55699 12.0279 6.79449 12.2196 6.98616C12.4112 7.17783 12.6487 7.27366 12.9321 7.27366ZM1.77832 15.8892H7.77832V8.38916H1.77832V15.8892Z"
                                    fill="#e55a2b"
                                  />
                                </svg>

                                {/* startDate={auctions[0]?.startDate} */}
                                {car.vehicle?.fuelType?.name || "-"}
                              </div>
                              <div className="flex flex-col items-center text-[10px] sm:text-[11px] w-1/4">
                                <svg
                                  className="w-3 h-3 sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]"
                                  viewBox="0 0 22 22"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M14.6187 1.60034C14.9661 1.54206 15.3765 1.59822 15.7065 1.76245L15.8433 1.8396L15.8442 1.84058C15.8639 1.85357 15.8936 1.87829 15.9263 1.90698C15.9616 1.93801 16.0078 1.98008 16.063 2.03101C16.1734 2.13293 16.3232 2.2736 16.5034 2.44604C16.8642 2.79124 17.3495 3.26429 17.896 3.80542L18.896 4.79858C19.1521 5.0552 19.3388 5.24445 19.4761 5.3894C19.7484 5.67706 19.8368 5.79741 19.897 5.92065C20.0743 6.27344 20.1338 6.66199 20.0747 7.01929V7.02026C20.052 7.15075 20.0374 7.26297 19.9849 7.3855C19.932 7.50884 19.8436 7.63774 19.6851 7.82202C19.3696 8.18864 18.7453 8.80779 17.4692 10.0847C16.4484 11.107 15.855 11.6941 15.4917 12.0398C15.1318 12.3823 14.9885 12.4964 14.8696 12.5603L14.8677 12.5613C14.4483 12.7788 14.023 12.8325 13.5972 12.7214H13.5962C13.3863 12.6639 13.2318 12.6038 13.0444 12.4705C12.8625 12.3409 12.6493 12.1423 12.3179 11.8152L11.7661 11.2712L6.95361 16.074L7.01123 16.1892L7.07764 16.3328C7.22022 16.6682 7.27686 17.0109 7.27686 17.4587C7.27684 17.7399 7.2585 17.9587 7.21045 18.1677C7.16237 18.3769 7.08588 18.5714 6.97314 18.8044H6.97217C6.58547 19.6014 5.86111 20.1929 4.98975 20.4226H4.98877C4.56556 20.5305 3.88838 20.5306 3.46631 20.4226H3.46533C1.64925 19.9383 0.687092 17.9582 1.41553 16.2654L1.49072 16.1023C1.57086 15.9382 1.63365 15.8249 1.7124 15.7156C1.79063 15.607 1.88246 15.5053 2.01709 15.363C2.41953 14.9364 2.86556 14.6729 3.43115 14.5095H3.43311C3.63603 14.455 3.92569 14.4248 4.20654 14.4207C4.48624 14.4165 4.77322 14.4375 4.96826 14.4939H4.96729L5.13623 14.5398L5.26416 14.4304C5.36971 14.3343 6.5075 13.2135 7.81494 11.9089L10.1157 9.61304L9.68311 9.17456C9.41768 8.90603 9.24582 8.71824 9.13037 8.54858C9.01192 8.37452 8.95353 8.22237 8.90771 8.03003C8.80575 7.59706 8.88976 7.06841 9.11084 6.69507L9.11279 6.69214H9.11377C9.12747 6.67072 9.15917 6.63441 9.19385 6.59644C9.23238 6.55425 9.28491 6.49779 9.3501 6.42944C9.48102 6.29218 9.66361 6.10437 9.88428 5.87964C10.326 5.42976 10.9227 4.83028 11.5679 4.19019C12.639 3.12504 13.2299 2.5396 13.5786 2.20972C13.9207 1.8861 14.0416 1.79626 14.1655 1.74292L14.3921 1.65601C14.4737 1.62966 14.5559 1.60832 14.6187 1.59937V1.60034ZM4.93994 15.4275C4.30037 15.2101 3.60179 15.2937 3.05615 15.6589H3.05518C2.92841 15.7425 2.77505 15.8831 2.63623 16.0388C2.4973 16.1947 2.3802 16.357 2.31982 16.4812V16.4822C1.83075 17.4722 2.139 18.6249 3.05615 19.24H3.05518C3.2503 19.3701 3.41811 19.4526 3.59814 19.5037C3.77879 19.5549 3.97868 19.5759 4.2417 19.5759C4.50448 19.5759 4.70451 19.555 4.88525 19.5037C5.06542 19.4525 5.23273 19.3695 5.42725 19.24C6.04539 18.8184 6.38916 18.1902 6.38916 17.4568C6.38906 16.8932 6.20075 16.4008 5.82666 16.0046C5.55389 15.7185 5.29198 15.5473 4.93994 15.4275ZM8.39307 12.6101C7.72024 13.2815 7.10872 13.8935 6.66553 14.3406C6.444 14.5641 6.26488 14.7462 6.14111 14.8738C6.09847 14.9177 6.06227 14.955 6.03369 14.9851C6.04295 14.9924 6.05284 15.0002 6.06299 15.0085C6.11571 15.052 6.18368 15.1117 6.25244 15.1765H6.25146L6.42041 15.323L8.78076 12.9666L11.1226 10.6169L10.9448 10.4392L10.7573 10.2576L8.39307 12.6101ZM12.3062 4.74487C12.2395 4.80941 12.1431 4.90294 12.0259 5.01831C11.7916 5.24895 11.4715 5.56659 11.1235 5.91382C10.5165 6.51901 10.2004 6.83637 10.0288 7.02612C9.94313 7.12088 9.89762 7.17911 9.87061 7.22241C9.84598 7.2619 9.83448 7.29051 9.82275 7.33081L9.82373 7.33179C9.78917 7.45351 9.77397 7.53684 9.77393 7.61597C9.77393 7.69517 9.78914 7.7783 9.82373 7.90015H9.82275C9.83407 7.9384 9.84532 7.96671 9.87842 8.01538C9.91502 8.06915 9.97895 8.14639 10.1001 8.2771C10.3417 8.53773 10.7946 8.99418 11.6704 9.87183L12.8384 11.0349C13.1261 11.3165 13.3255 11.5032 13.4751 11.6267C13.6236 11.7493 13.714 11.8013 13.7856 11.8259C13.8555 11.8499 13.9161 11.8503 14.0239 11.8503C14.1315 11.8482 14.1987 11.8457 14.2661 11.8259C14.3321 11.8066 14.4069 11.7687 14.5171 11.6843C14.7428 11.5116 15.0879 11.1697 15.7798 10.4792L16.938 9.32202L14.6392 7.0271H14.6382C13.9856 6.37172 13.3887 5.77681 12.9546 5.34546C12.7376 5.1299 12.561 4.95514 12.439 4.83374C12.3944 4.78943 12.3572 4.75198 12.3276 4.72241C12.3206 4.72918 12.314 4.73725 12.3062 4.74487ZM12.9722 4.06909L17.5874 8.67456L18.1509 8.11304L16.1831 6.15796V6.15894C15.0507 5.03641 13.9749 3.96353 13.7866 3.78296L13.7856 3.78198L13.519 3.52222L12.9722 4.06909ZM15.2876 2.56226C14.9587 2.39873 14.5383 2.48374 14.2524 2.79077L14.2495 2.79468L14.1665 2.87671L18.7993 7.46948L18.8921 7.38159C19.0032 7.27467 19.0759 7.17604 19.1216 7.07397C19.167 6.97236 19.188 6.86043 19.188 6.72339C19.188 6.61077 19.1883 6.55009 19.1655 6.4812C19.1427 6.41196 19.0929 6.32561 18.9731 6.1814C18.7317 5.89078 18.2413 5.40673 17.2368 4.41284V4.41187C16.5676 3.74887 16.105 3.29596 15.7944 3.00171C15.6391 2.85449 15.5226 2.74803 15.438 2.67554C15.3959 2.63947 15.363 2.61239 15.3374 2.59351C15.3246 2.58408 15.314 2.57695 15.3062 2.57202C15.3026 2.56978 15.2996 2.56835 15.2974 2.56714C15.2962 2.56653 15.295 2.56549 15.2944 2.56519H15.2935L15.2876 2.56226Z"
                                    fill="#E95923"
                                    stroke="#E95923"
                                    stroke-width="0.233657"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M12.9655 7.92444C12.7354 8.02765 12.5454 8.31201 12.5454 8.53213C12.5454 8.77647 12.7586 9.06083 13.0045 9.15561C13.5587 9.36836 14.0811 8.78489 13.8363 8.23197C13.6853 7.92444 13.2737 7.77384 12.9655 7.92444Z"
                                    fill="#E95923"
                                  />
                                </svg>

                                {/* <span>{car.cylinder || "-"}</span> */}
                                {car?.vehicle?.cylinder?.name || "-"}
                              </div>
                              <div className="flex flex-col items-center text-[10px] sm:text-[11px] w-1/4">
                                <svg
                                  className="w-3 h-3 sm:w-4 sm:h-4 md:w-[18px] md:h-[18px]"
                                  viewBox="0 0 22 22"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <g clip-path="url(#clip0_1730_33991)">
                                    <path
                                      d="M13.1456 6.40362V4.6354H15.0927V2.86719H9.64062V4.6354H11.5878V6.40362"
                                      stroke="#E95923"
                                      stroke-width="0.934628"
                                      stroke-miterlimit="10"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M4.96705 12.1496H3.40931V9.49731H1.85156V16.5702H3.40931V13.9179H4.96705"
                                      stroke="#E95923"
                                      stroke-width="0.934628"
                                      stroke-miterlimit="10"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M18.9865 9.05426V11.2645H17.8182V7.6587C17.8182 7.20913 17.5024 6.84399 17.1137 6.84399H9.2467C9.06016 6.84399 8.88024 6.93019 8.74822 7.08359L6.91398 9.49632H5.74567C5.35623 9.49632 4.9668 9.93837 4.9668 10.3804V15.6851C4.9668 16.1351 5.35701 16.5692 5.74567 16.5692H6.91398L8.749 18.9832C8.88102 19.1357 9.06016 19.2215 9.2467 19.2215H14.2529C14.4394 19.2215 14.6186 19.1357 14.7506 18.9832L17.6114 15.6758C17.7442 15.5228 17.8178 15.316 17.8178 15.0998V13.9169H18.9861V16.1271H20.5439V9.05426H18.9865Z"
                                      stroke="#E95923"
                                      stroke-width="0.934628"
                                      stroke-miterlimit="10"
                                      stroke-linecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M13.9237 12.5879L10.8082 17.4505L11.9765 13.4721H10.0293L11.9765 9.05151H13.9237L11.9765 12.5879H13.9237Z"
                                      fill="#E95923"
                                    />
                                  </g>
                                  <defs>
                                    <clipPath id="clip0_1730_33991">
                                      <rect
                                        width="21.0291"
                                        height="21.0291"
                                        fill="white"
                                        transform="matrix(1 0 0 -1 0.683594 21.5559)"
                                      />
                                    </clipPath>
                                  </defs>
                                </svg>

                                {car?.vehicle?.engine?.name}
                              </div>
                            </div>
                            {/* ...Bid Now & Details buttons... */}

                            <div className="flex justify-between gap-x-4 mt-2">
                              {/* Bid Now Button */}
                              <button
                                onClick={() => handleBidNow(car.vehicle)}
                                className="bg-primary text-white
                            text-[11px] flex items-center h-[30px] w-[100px] justify-center gap-1"
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 22 22"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="inline-block"
                                >
                                  <path
                                    d="M16.1159 6.41514L13.6328 3.94641C14.3039 3.41263 15.2435 3.47935 15.9146 4.07986C16.2501 4.41347 16.4514 4.88052 16.4514 5.34758C16.4514 5.68119 16.3172 6.08153 16.1159 6.41514Z"
                                    stroke="white"
                                    strokeWidth="0.835"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M12.5566 10.1467L3.89941 19.2877C3.22831 19.9549 2.15455 19.9549 1.48345 19.2877C0.812349 18.6204 0.745239 17.4862 1.41634 16.8189L10.0735 7.67798L12.5566 10.1467Z"
                                    stroke="white"
                                    strokeWidth="0.835"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M17.2513 7.54832L13.6945 11.2848L8.92969 6.48076L12.4865 2.81104L17.2513 7.54832Z"
                                    stroke="white"
                                    strokeWidth="0.835"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M7.32198 6.87749C6.98642 6.54388 6.98642 6.0101 7.32198 5.67649L11.617 1.13937C11.9526 0.805757 12.5566 0.805757 12.8921 1.13937C13.2277 1.47298 13.2277 2.00676 12.8921 2.34037L8.59707 6.87749C8.26152 7.2111 7.65753 7.2111 7.32198 6.87749Z"
                                    stroke="white"
                                    strokeWidth="0.835"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M13.2907 12.9517C12.9552 12.6181 12.9552 12.0843 13.2907 11.7507L17.5858 7.21359C17.9213 6.87998 18.5253 6.87998 18.8609 7.21359C19.1964 7.5472 19.1964 8.08098 18.8609 8.41459L14.5658 12.885C14.2303 13.2853 13.6934 13.2853 13.2907 12.9517Z"
                                    stroke="white"
                                    strokeWidth="0.835"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M20.4109 20.8905H10.0759C9.8075 20.8905 9.53906 20.6236 9.53906 20.3567V19.4893C9.53906 19.2225 9.8075 18.9556 10.0759 18.9556H20.4109C20.6793 18.9556 20.9478 19.2225 20.9478 19.4893V20.3567C20.9478 20.6236 20.7464 20.8905 20.4109 20.8905Z"
                                    stroke="white"
                                    strokeWidth="0.835"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M19.9377 18.9569H10.6094V17.6225C10.6094 17.2889 10.8778 17.022 11.2134 17.022H19.3337C19.6692 17.022 19.9377 17.2889 19.9377 17.6225V18.9569Z"
                                    stroke="white"
                                    strokeWidth="0.835"
                                    strokeMiterlimit="10"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                {translate("buttons.preBid")}
                              </button>
                              {/* Details Button */}
                              {/* <Link
                            to={car.slug ? `/${car.slug}` : `/car/${car.id}`}
                            className='text-[#405FF2] flex items-center justify-center gap-[5px] font-[500] text-[15px] h-[48px] px-4'
                          >
                            Details 1
                          </Link> */}
                              <button
                                className="text-[#ffffff] bg-darkGraybg  flex text-[11px]  items-center justify-center h-[30px] w-[100px] gap-1"
                                onClick={() =>
                                  navigate(`/${car.vehicle.slug} `)
                                }
                              >
                                {translate("buttons.details")}
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 25 25"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M14.7148 6.56738C14.7148 6.15317 14.3791 5.81738 13.9648 5.81738H4.96484C4.55063 5.81738 4.21484 6.15317 4.21484 6.56738C4.21484 6.9816 4.55063 7.31738 4.96484 7.31738H12.1542L1.43451 18.0371C1.14162 18.3299 1.14162 18.8048 1.43451 19.0977C1.72741 19.3906 2.20228 19.3906 2.49517 19.0977L13.2148 8.37804V15.5674C13.2148 15.9816 13.5506 16.3174 13.9648 16.3174C14.3791 16.3174 14.7148 15.9816 14.7148 15.5674V6.56738Z"
                                    fill="#ffffff"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="absolute bottom-0 sm:bottom-2 md:bottom-2 right-2 sm:right-4 flex items-center gap-2 z-10">
                <div className="flex justify-end items-center px-2 gap-2">
                  <a
                    href={`/vehicles?auctionId=${selectedAuction?.id}`}
                    className="leading-6 text-sm text-white hover:underline"
                  >
                    {translate("buttons.viewAll")}
                  </a>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 25 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M14.7148 6.56738C14.7148 6.15317 14.3791 5.81738 13.9648 5.81738H4.96484C4.55063 5.81738 4.21484 6.15317 4.21484 6.56738C4.21484 6.9816 4.55063 7.31738 4.96484 7.31738H12.1542L1.43451 18.0371C1.14162 18.3299 1.14162 18.8048 1.43451 19.0977C1.72741 19.3906 2.20228 19.3906 2.49517 19.0977L13.2148 8.37804V15.5674C13.2148 15.9816 13.5506 16.3174 13.9648 16.3174C14.3791 16.3174 14.7148 15.9816 14.7148 15.5674V6.56738Z"
                      fill="#ffffff"
                    />
                  </svg>
                </div>
                <button
                  className="bg-[#FFFFFF] text-white p-2 rounded-[5px] shadow hover:bg-gray-600"
                  onClick={scrollLeft}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 15 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M14.3501 0.792785C13.9591 0.473058 13.3704 0.516256 13.0352 0.889271L5.57502 9.19182C5.27569 9.52495 5.27569 10.0165 5.57502 10.3497L13.0352 18.6522C13.3704 19.0252 13.9591 19.0684 14.3501 18.7487C14.7412 18.429 14.7864 17.8674 14.4513 17.4944L7.51125 9.77074L14.4513 2.04711C14.7864 1.67409 14.7412 1.11251 14.3501 0.792785ZM9.37641 0.792908C8.98538 0.473181 8.39668 0.516379 8.06151 0.889394L0.601316 9.19195C0.301986 9.52508 0.301986 10.0167 0.601316 10.3498L8.06151 18.6523C8.39668 19.0254 8.98538 19.0686 9.37641 18.7488C9.76745 18.4291 9.81273 17.8675 9.47756 17.4945L2.53755 9.77086L9.47756 2.04723C9.81273 1.67421 9.76745 1.11264 9.37641 0.792908Z"
                      fill="black"
                      fill-opacity="0.6"
                    />
                  </svg>
                </button>
                <div className="text-white text-[14px] w-5 text-center">
                  {cardIndex + 1}
                </div>
                <button
                  className="bg-[#FFFFFF] text-black p-2 rounded-[5px] shadow hover:bg-gray-600"
                  onClick={scrollRight}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M1.24168 0.792785C1.63271 0.473058 2.22141 0.516256 2.55658 0.889271L10.0168 9.19182C10.3161 9.52495 10.3161 10.0165 10.0168 10.3497L2.55658 18.6522C2.22141 19.0252 1.63271 19.0684 1.24168 18.7487C0.850647 18.429 0.805362 17.8674 1.14053 17.4944L8.08054 9.77074L1.14053 2.04711C0.805362 1.67409 0.850647 1.11251 1.24168 0.792785ZM6.21538 0.792908C6.60641 0.473181 7.19512 0.516379 7.53029 0.889394L14.9905 9.19195C15.2898 9.52508 15.2898 10.0167 14.9905 10.3498L7.53029 18.6523C7.19512 19.0254 6.60641 19.0686 6.21538 18.7488C5.82435 18.4291 5.77907 17.8675 6.11424 17.4945L13.0542 9.77086L6.11424 2.04723C5.77907 1.67421 5.82435 1.11264 6.21538 0.792908Z"
                      fill="black"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Prebid Popup Modal using BidInformation component */}
      {showBidNowPopup && selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Bid Now Summary
              </h3>
              <button
                onClick={closeBidNowPopup}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Vehicle Info */}
            <div className="mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">
                {selectedVehicle.name}
              </h4>
              <div className="flex justify-between text-sm text-gray-600">
                <span>
                  Lot No:{" "}
                  <span className="font-semibold text-orange-600">
                    {selectedVehicle.lotNumber}
                  </span>
                </span>
                {selectedVehicle.auctionLanesVehicles?.[0]?.runNo && (
                  <span>
                    Run No:{" "}
                    <span className="font-semibold text-orange-600">
                      {selectedVehicle.auctionLanesVehicles[0].runNo}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* BidInformation Component */}
            {(() => {
              console.log(
                "Rendering BidInformation with vehicle:",
                selectedVehicle,
              );
              console.log("Vehicle latestBid:", selectedVehicle?.latestBid);
              return (
                <BidInformation
                  key={`${selectedVehicle.id}-${selectedVehicle.latestBid}`} // Force re-render when latestBid changes
                  vehicle={selectedVehicle}
                  prebidTimeLeft={0} // You can calculate this if needed
                  bidAmount={bidAmount}
                  setBidAmount={setBidAmount}
                  bidIncrement={selectedVehicle?.bidIncrement || 100}
                  isSubmittingBid={isSubmitting}
                  handlePreBidSubmit={handleBidNowSubmit}
                  getMinBidAmount={() => {
                    // Minimum bid should be current bid + increment, or starting bid if no current bid
                    const currentLatestBid =
                      parseFloat(selectedVehicle?.latestBid) || 0;
                    return currentLatestBid > 0
                      ? currentLatestBid +
                          (selectedVehicle?.bidIncrement || 100)
                      : selectedVehicle?.startingBid;
                  }}
                />
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
};

export default AuctionComponent;
